<template>
  <UikitSwiper
    ref="$swiper"
    class="osk-stories-group-slider"
    :pagination="swiperPaginationOptions"
    @slide-change="onSlideChange"
  >
    <UikitLoaderInner v-if="!mainStory" />

    <template
      v-for="(story, i) in mainStory?.slideList"
      :key="i"
    >
      <UikitSwiperSlide
        v-if="story.contentURL"
        class="osk-stories-group-slider__slide"
        :style="{
          'background-image': `url(${story.contentURL}`
        }"
      >
        <div
          v-if="mainStory?.title"
          class="osk-stories-group-slider__title"
        >
          {{ mainStory.title }}
        </div>

        <nuxt-link
          v-if="mainStory && story.userId"
          class="osk-stories-group-slider__slide-user"
          :to="`/profile/${story.userId}`"
          @click="handleUserClick(story)"
        >
          <div
            class="osk-stories-group-slider__slide-user-avatar"
            :style="{
              'background-image': `url(${mainStory.avatarPath}`
            }"
          >
            <span
              v-if="!mainStory.avatarPath"
              class="osk-stories-group-slider__slide-user-avatar-text"
            >
              {{ firstCharComp }}
            </span>
          </div>

          <div class="osk-stories-group-slider__slide-user-text">
            {{ mainStory.nickName }}
          </div>

          <div
            v-if="storyTime"
            class="osk-stories-group-slider__slide-user-storyTime"
          >
            {{ storyTime }}
          </div>
        </nuxt-link>

        <template
          v-for="(tag, index) in story.tagDTOList"
          :key="index"
        >
          <nuxt-link
            v-if="tag.tagType === 'PRODUCT'"
            class="osk-stories-group-slider__tag"
            :to="`/products/${tag.productId}`"
            :style="{
              top: `${tag.y}%`,
              left: `${tag.x}%`,
            }"
            @click="handleProductClick(tag, story)"
          >
            <div
              class="osk-stories-group-slider__tag-name"
            >
              {{ tag.tagName }}
            </div>

            <div class="osk-stories-group-slider__tag-prices">
              <span
                v-if="tag.price"
                class="osk-stories-group-slider__tag-price"
              >
                {{ formatPrice(tag.price) }}&nbsp;₽
              </span>

              <span
                v-if="tag.oldPrice"
                class="osk-stories-group-slider__tag-oldPrice"
              >
                {{ tag.oldPrice }}&nbsp;₽
              </span>
            </div>
          </nuxt-link>
        </template>

        <div
          v-for="(sticker, index) in story.stickerDTOList"
          :key="index"
          class="osk-stories-group-slider__sticker"
          :style="{
            top: `${sticker.y}%`,
            left: `${sticker.x}%`,
            width: `${sticker.width}%`,
            height: `${sticker.height}%`,
            transform: `scale(${sticker.scale}) rotate(${sticker.angle}rad) translate(-50%, -50%)`,
            'background-image': `url(${sticker.stickerURL}`
          }"
        >
&nbsp;
        </div>

        <div
          class="osk-stories-group-slider__prev"
          @click="handlePrev"
        >
&nbsp;
        </div>

        <div
          class="osk-stories-group-slider__next"
          @click="handleNext"
        >
&nbsp;
        </div>
      </UikitSwiperSlide>
    </template>

    <div class="swiper-pagination osk-stories-group-slider__pagination">
&nbsp;
    </div>
  </UikitSwiper>
</template>

<script lang="ts" setup>
import { PaginationOptions, Swiper as SwiperClass } from 'swiper/types';

import {
  StoryDTO, ResponseSlideDTO, ContentType, TagDTO,
} from '~/restAPI/data-contracts';

import { AnalyticsEvents } from '~/types/analytics';

interface IAnalytics {
  chapter?: ContentType;
  chapter_index?: number;
  segment_id?: number;
  id_chapter?: string;
  item?: string;
}

const props = defineProps<{
  mainStory?: StoryDTO
  analytics?: IAnalytics
}>();

const emits = defineEmits<{
  (e: 'slideChange', swiper: SwiperClass): void
  (e: 'slidePrev'): void
  (e: 'slideNext'): void
}>();

const $swiper = ref();

const { $addEvent } = useNuxtApp();
const { getUserOnOskellyTime } = useDateFormatter();

const storyTime = computed(() => {
  if (!props.mainStory?.createDate) return '';

  const date = new Date(props.mainStory.createDate);

  return getUserOnOskellyTime(date);
});

const swiperPaginationOptions: PaginationOptions = {
  el: '.osk-stories-group-slider__pagination',
  type: 'bullets',
  clickable: false,
  renderBullet: (index: number, className: string) => `<div class="${className}"><span class="osk-stories-group-slider__pagination-span"></span></div>`,
};

const firstCharComp = computed(() => (props.mainStory?.nickName?.slice(0, 1) ?? 'U'));

function onSlideChange(swiper: SwiperClass) {
  emits('slideChange', swiper);
}

function handleUserClick(story: ResponseSlideDTO) {
  if (props.analytics) {
    $addEvent(AnalyticsEvents.ClickEvent, {
      ...props.analytics,
      action: 'profile',
      page: 'stories',
      id_item: story.id,
    });
  }
}

function handleProductClick(tag: TagDTO, story: ResponseSlideDTO) {
  if (props.analytics) {
    $addEvent(AnalyticsEvents.ClickEvent, {
      ...props.analytics,
      action: 'item',
      product_id: String(tag.productId),
      page: 'stories',
      id_item: story.id,
    });
  }
}

function handlePrev() {
  const swiper: SwiperClass = $swiper.value?.swiper;

  if (!swiper) return;

  swiper.slidePrev();

  if (swiper.activeIndex === swiper.clickedIndex) {
    emits('slidePrev');
  }
}

function handleNext() {
  const swiper: SwiperClass = $swiper.value?.swiper;

  if (!swiper) return;

  swiper.slideNext();

  if (swiper.activeIndex === swiper.clickedIndex) {
    emits('slideNext');
  }
}
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-stories-group-slider {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;

  @include media-query(md-and-up) {
    border-radius: 4px;
  }

  .osk-swiper__block {
    height: 100%;
  }

  &__slide {
    position: relative;
    width: 100% !important;
    height: 100% !important;
    overflow: hidden;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;

    // &:after,
    &:before {
      content: '';
      width: 100%;
      height: 15%;
      position: absolute;
      left: 0;
      z-index: 1;
    }

    &:before {
      top: 0;
      background: linear-gradient(0deg, rgba(17, 17, 17, 0.77) 0.07%, #111 2.49%,
      rgba(17, 17, 17, 0.67) 40.00%, rgba(17, 17, 17, 0.56) 46.67%,
      rgba(17, 17, 17, 0.44) 53.33%, rgba(17, 17, 17, 0.33) 60.00%, rgba(17, 17, 17, 0.23) 66.67%,
      rgba(17, 17, 17, 0.15) 73.33%, rgba(17, 17, 17, 0.08) 80.00%, rgba(17, 17, 17, 0.04) 86.67%, rgba(17, 17, 17, 0.01) 93.33%, rgba(17, 17, 17, 0.00) 100%);
      transform: rotate(180deg);
    }

    // &:after {
    //   bottom: 0;
    //   background: linear-gradient(0deg, rgba(17, 17, 17, 0.77) 0.07%, #111 2.49%, rgba(17, 17, 17, 0.67) 40.00%,
    //   rgba(17, 17, 17, 0.56) 46.67%, rgba(17, 17, 17, 0.44) 53.33%, rgba(17, 17, 17, 0.33) 60.00%, rgba(17, 17, 17, 0.23) 66.67%,
    //   rgba(17, 17, 17, 0.15) 73.33%, rgba(17, 17, 17, 0.08) 80.00%, rgba(17, 17, 17, 0.04) 86.67%, rgba(17, 17, 17, 0.01) 93.33%, rgba(17, 17, 17, 0.00) 100%);
    // }
  }

  &__pagination.swiper-pagination {
    display: flex;
    justify-content: space-between;
    position: absolute;
    top: 9px !important;
    left: 0;
    right: 0;
    width: 100%;
    height: calc(100% - 10px);
    z-index: 100;
    opacity: 0;
    transition: opacity 0.8s ease;
    padding: 0 16px;

    .swiper-pagination-bullet {
      position: relative;
      width: 100%;
      min-width: 1px;
      flex-shrink: 10;
      border-radius: 100px;
      height: 2px;
      background-color: rgba(255, 255, 255, .5);
      overflow: hidden;
      margin-right: 2px !important;
      opacity: 1;

      &:last-child {
        margin-right: 0 !important;
      }

      &:only-child {
        display: inline-block !important;
      }

      .osk-stories-group-slider__pagination-span {
        position: absolute;
        background-color: #fff;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        border-radius: 100px;
        transform: translateX(-100%);
        transition: transform 0.3s ease;
      }
    }
  }

  &__tag {
    position: absolute;
    border-radius: 2px;
    background: rgba(0, 0, 0, 0.40);
    padding: 12px;
    color: #fff;
    user-select: none;
    z-index: 2;
    transform: translate(-50%, -50%);
  }

  &__tag-name {
    font-weight: 600;
    margin-bottom: 10px;
    line-height: normal;
  }

  &__tag-prices {
    display: flex;
    align-items: baseline;
  }

  &__tag-price {
    margin-right: 4px;
    font-weight: 600;
    line-height: normal;
  }

  &__tag-oldPrice {
    font-size: 12px;
    font-weight: 400;
    line-height: normal;
    text-decoration: line-through;
    opacity: 0.6;
  }

  &__sticker {
    position: absolute;
    background-size: contain;
    background-position: center center;
    background-repeat: no-repeat;
    user-select: none;
    z-index: 2;
  }

  &__slide-user {
    position: absolute;
    display: flex;
    align-items: center;
    top: 25px;
    left: 16px;
    z-index: 2;
    user-select: none;
  }

  &__slide-user-avatar {
    width: 33px;
    height: 33px;
    border-radius: 100px;
    overflow: hidden;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
  }

  &__slide-user-avatar-text {
    display: block;
    width: 100%;
    height: 100%;
    display: flex;
    user-select: none;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    background-color: #000;
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    color: #fff;
    flex-shrink: 0;
  }

  &__slide-user-avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__slide-user-text {
    color: #fff;
    margin-left: 9px;
    font-weight: 600;
  }

  &__slide-user-storyTime {
    color: #fff;
    font-weight: 400;
    opacity: 0.5;
    margin-left: 9px;
  }

  &__title {
    text-align: center;
    font-weight: 600;
    color: #fff;
    position: absolute;
    bottom: 16px;
    width: 100%;
    z-index: 2;
    text-shadow: 0px 2px 9px rgba(0, 0, 0, 0.40);
  }

  &.osk-swiper {
    .osk-swiper-button-prev,
    .osk-swiper-button-next {
      width: 50%;
      left: 0;
      right: auto;
      top: 0;
      height: 100%;
      transform: none;
      z-index: 1;
      background: red;

      .osk-icon {
        display: none;
      }
    }

    .osk-swiper-button-next {
      left: auto;
      right: 0;
      background: blue;
    }
  }

  &__prev {
    position: absolute;
    left: 0;
    width: 50%;
    top: 0;
    bottom: 0;
    opacity: .5;
    z-index: 1;
  }

  &__next {
    position: absolute;
    right: 0;
    width: 50%;
    top: 0;
    bottom: 0;
    opacity: .5;
    z-index: 1;
  }
}

.osk-stories-main-slider__item {
  &:not(.swiper-slide-active) {
    .osk-stories-group-slider__prev,
    .osk-stories-group-slider__next {
      display: none;
    }

    .osk-stories-group-slider__slide:before {
      @include media-query(md-and-up) {
        display: none;
      }
    }
  }

  // &.swiper-slide-active {
  //   .osk-stories-group-slider__slide:after {
  //     display: none;
  //   }
  // }
}
</style>
